// Demo exports: the accountant's pack (a real zip of CSVs) and a SAF-T file,
// both built from the demo trial balance and marked as demo data. The real
// ones come from ledger.accountant_pack and ledger.saft_xml.
import { TRIAL_BALANCE } from './mockDocuments';
import type { BookExport, LedgerSource } from './source';

type ExportsPart = Pick<LedgerSource, 'exportSaft' | 'exportAccountantPack' | 'bookExports'>;

const delay = <T,>(v: T, ms = 150): Promise<T> => new Promise((r) => setTimeout(() => r(v), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

let log: BookExport[] = [];
export function resetDemoExports() { log = []; }

const csvField = (v: string) => (/^[=+@\t\r-]/.test(v) && !/^-?\d+(\.\d+)?$/.test(v) ? `"'${v.replace(/"/g, '""')}"`
  : /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
const csv = (rows: string[][]) => rows.map((r) => r.map(csvField).join(',')).join('\r\n') + '\r\n';
const money = (v: string) => (Number(v) === 0 ? '' : Number(v).toFixed(2));
const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function check(from: string, to: string) {
  if (!from || !to || to < from) return 'Choose a start date on or before the end date';
  if ((Date.parse(to) - Date.parse(from)) / 86_400_000 > 731) return 'Export at most two years at a time';
  return null;
}

async function record(kind: BookExport['kind'], from: string, to: string, bytes: Uint8Array) {
  let sha = '';
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
    sha = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { /* no subtle crypto here: leave it blank in the demo */ }
  log.unshift({ kind, from, to, created_by: 'ledger:user:brian', created_at: new Date().toISOString(), sha256: sha, bytes: bytes.length });
}

// ---- a minimal zip (stored, not compressed): enough for a demo download ----
const CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
const crc32 = (b: Uint8Array) => { let c = 0xffffffff; for (const x of b) c = CRC[(c ^ x) & 0xff]! ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; };

export function zipStored(files: { name: string; data: Uint8Array }[]): Uint8Array {
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const name = new TextEncoder().encode(f.name);
    const crc = crc32(f.data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); local.setUint16(4, 20, true); local.setUint16(6, 0x0800, true);
    local.setUint32(14, crc, true); local.setUint32(18, f.data.length, true); local.setUint32(22, f.data.length, true);
    local.setUint16(26, name.length, true);
    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, 0x02014b50, true); dir.setUint16(4, 20, true); dir.setUint16(6, 20, true); dir.setUint16(8, 0x0800, true);
    dir.setUint32(16, crc, true); dir.setUint32(20, f.data.length, true); dir.setUint32(24, f.data.length, true);
    dir.setUint16(28, name.length, true); dir.setUint32(42, offset, true);
    parts.push(new Uint8Array(local.buffer), name, f.data);
    central.push(new Uint8Array(dir.buffer), name);
    offset += 30 + name.length + f.data.length;
  }
  const size = central.reduce((n, p) => n + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, size, true); end.setUint32(16, offset, true);
  const all = [...parts, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of all) { out.set(p, at); at += p.length; }
  return out;
}

export const demoExports: ExportsPart = {
  exportAccountantPack: async (from, to) => {
    const problem = check(from, to);
    if (problem) return fail(problem);
    const enc = new TextEncoder();
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const withBom = (text: string) => { const b = enc.encode(text); const o = new Uint8Array(b.length + 3); o.set(bom); o.set(b, 3); return o; };
    const dr = TRIAL_BALANCE.reduce((n, l) => n + Number(l.debit ?? 0), 0);
    const cr = TRIAL_BALANCE.reduce((n, l) => n + Number(l.credit ?? 0), 0);
    const files = [
      { name: 'README.txt', data: enc.encode(`Blaze Fire Systems Ltd\r\nAccounts from ${from} to ${to}, in EUR\r\nDEMO DATA: the real pack has the nominal ledger, profit and loss, customer and supplier balances, VAT returns and manual journals too.\r\n\r\nThe trial balance ${dr.toFixed(2) === cr.toFixed(2) ? 'balances' : 'does NOT balance'}.\r\n`) },
      { name: 'trial-balance.csv', data: withBom(csv([['Code', 'Account', 'Type', 'Closing debit', 'Closing credit'],
        ...TRIAL_BALANCE.map((l) => [l.code ?? '', l.name, (l.class ?? '').replace(/^./, (c) => c.toUpperCase()), money(l.debit ?? '0'), money(l.credit ?? '0')]),
        ['', 'Total', '', dr.toFixed(2), cr.toFixed(2)]])) },
    ];
    const zip = zipStored(files);
    await record('pack', from, to, zip);
    return delay(new Blob([zip as BlobPart], { type: 'application/zip' }));
  },
  exportSaft: async (from, to) => {
    const problem = check(from, to);
    if (problem) return fail(problem);
    const accounts = TRIAL_BALANCE.map((l) => {
      const bal = Number(l.debit ?? 0) - Number(l.credit ?? 0);
      return `<Account><AccountID>${l.code}</AccountID><AccountDescription>${esc(l.name)}</AccountDescription><AccountType>${(l.class ?? '').replace(/^./, (c) => c.toUpperCase())}</AccountType>`
        + `<OpeningDebitBalance>0.00</OpeningDebitBalance>`
        + (bal >= 0 ? `<ClosingDebitBalance>${bal.toFixed(2)}</ClosingDebitBalance>` : `<ClosingCreditBalance>${(-bal).toFixed(2)}</ClosingCreditBalance>`) + '</Account>';
    }).join('');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<AuditFile xmlns="urn:OECD:StandardAuditFile-Tax:2.00"><Header><AuditFileVersion>2.00</AuditFileVersion>`
      + `<AuditFileCountry>IE</AuditFileCountry><AuditFileDateCreated>${new Date().toISOString().slice(0, 10)}</AuditFileDateCreated>`
      + `<SoftwareCompanyName>Elysian</SoftwareCompanyName><SoftwareID>Elysian Ledger</SoftwareID><SoftwareVersion>demo</SoftwareVersion>`
      + `<Company><Name>Blaze Fire Systems Ltd</Name></Company><DefaultCurrencyCode>EUR</DefaultCurrencyCode>`
      + `<SelectionCriteria><SelectionStartDate>${from}</SelectionStartDate><SelectionEndDate>${to}</SelectionEndDate></SelectionCriteria>`
      + `<HeaderComment>DEMO DATA: accounts only.</HeaderComment><TaxAccountingBasis>Invoice</TaxAccountingBasis></Header>`
      + `<MasterFiles><GeneralLedgerAccounts>${accounts}</GeneralLedgerAccounts></MasterFiles></AuditFile>\n`;
    const bytes = new TextEncoder().encode(xml);
    await record('saft', from, to, bytes);
    return delay(new Blob([bytes as BlobPart], { type: 'application/xml' }));
  },
  bookExports: () => delay(log.slice(0, 10).map((e) => ({ ...e }))),
};
